import React, { useState, useEffect, useRef } from 'react';
import './BlogModal.css';
import { createBlogPost, updateBlogPost } from '../../api/api';

const BlogModal = ({
  post,
  onClose,
  isEditing: initialIsEditing,
  onSave,
  isAuthenticated,
}) => {
  const [isEditing, setIsEditing] = useState(!!initialIsEditing);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    title: post?.title || '',
    slug: post?.slug || '',
    excerpt: post?.excerpt || '',
    content: post?.content || '',
    category: post?.category || '',
    tags: post?.tags?.join(', ') || '',
    status: post?.status || 'DRAFT',
    publishedAt: post?.publishedAt
      ? new Date(post.publishedAt).toISOString().split('T')[0]
      : '',
    isFeatured: post?.isFeatured || false,
    featuredImage: null,
  });

  const fileInputRef = useRef(null);

  useEffect(() => {
    document.body.classList.add('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, []);

  const validateForm = (data = formData) => {
    const newErrors = {};

    if (!data.title.trim()) newErrors.title = 'Tytuł jest wymagany';

    if (!data.slug.trim()) newErrors.slug = 'Slug jest wymagany';
    else if (!/^[a-z0-9-]+$/.test(data.slug))
      newErrors.slug = 'Slug może zawierać tylko małe litery, cyfry i myślniki';

    if (!data.excerpt.trim()) newErrors.excerpt = 'Krótki opis jest wymagany';

    if (!data.content.trim()) newErrors.content = 'Treść wpisu jest wymagana';

    if (!data.category.trim()) newErrors.category = 'Kategoria jest wymagana';

    if (data.status === 'PUBLISHED' && !data.publishedAt) {
      newErrors.publishedAt =
        'Data publikacji jest wymagana dla opublikowanego wpisu';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    let newValue = value;
    if (type === 'file') newValue = files?.[0] || null;
    else if (type === 'checkbox') newValue = checked;

    setFormData((prev) => ({ ...prev, [name]: newValue }));

    if (errors[name]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let finalPublishedAt = formData.publishedAt;

    if (formData.status === 'PUBLISHED' && !finalPublishedAt) {
      finalPublishedAt = new Date().toISOString().split('T')[0];
    }

    const updatedFormData = {
      ...formData,
      publishedAt: finalPublishedAt,
    };

    setFormData(updatedFormData);

    if (!validateForm(updatedFormData)) {
      const firstError = document.querySelector('.error-text');
      if (firstError) {
        firstError.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    const form = new FormData();
    form.append('title', updatedFormData.title.trim());
    form.append('slug', updatedFormData.slug.trim());
    form.append('excerpt', updatedFormData.excerpt.trim());
    form.append('content', updatedFormData.content.trim());
    form.append('category', updatedFormData.category.trim());

    form.append(
      'tags',
      JSON.stringify(
        updatedFormData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean)
      )
    );

    form.append('status', updatedFormData.status);
    form.append('isFeatured', updatedFormData.isFeatured);

    if (updatedFormData.publishedAt) {
      form.append('publishedAt', updatedFormData.publishedAt);
    }

    if (updatedFormData.featuredImage) {
      form.append('featuredImage', updatedFormData.featuredImage);
    }

    try {
      let result;

      if (post?.id) {
        result = await updateBlogPost(post.id, form);
      } else {
        result = await createBlogPost(form);
      }

      localStorage.removeItem('blogDraft');

      onSave(result);
      setSaveSuccess(true);

      setTimeout(() => {
        onClose();
      }, 1800);
    } catch (err) {
      console.error('Błąd zapisu wpisu:', err);

      setErrors((prev) => ({
        ...prev,
        submit:
          err?.response?.data?.error ||
          err.message ||
          'Nie udało się zapisać wpisu.',
      }));
    }
  };

  const renderViewMode = () => (
    <>
      <h2>{post.title}</h2>

      {post.featuredImageUrl && (
        <img
          src={post.featuredImageUrl}
          alt={post.title}
          className="featured-image"
        />
      )}

      <div className="post-meta">
        <p>
          <strong>Kategoria:</strong> {post.category || '—'}
        </p>
        <p>
          <strong>Tagi:</strong>{' '}
          {post.tags?.length ? post.tags.join(' • ') : '—'}
        </p>
        <p>
          <strong>Status:</strong> {post.status}
        </p>
        <p>
          <strong>Data publikacji:</strong>{' '}
          {post.publishedAt
            ? new Date(post.publishedAt).toLocaleDateString('pl-PL', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })
            : 'Brak daty'}
        </p>
      </div>

      <div className="post-content">
        {post.content?.split('\n').map((line, i) => <p key={i}>{line}</p>) || (
          <p>Brak treści</p>
        )}
      </div>
    </>
  );

  return (
    <div className="modal-overlay active" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>

        {saveSuccess ? (
          <div className="success-message">
            <h2>Zapisano pomyślnie!</h2>
            <p>Modal zamknie się za chwilę...</p>
          </div>
        ) : isEditing ? (
          <form onSubmit={handleSubmit} className="blog-form" noValidate>
            <h2>{post ? 'Edytuj wpis' : 'Nowy wpis'}</h2>

            <div className="form-group">
              <label>Tytuł *</label>
              <input
                name="title"
                value={formData.title}
                onChange={handleChange}
              />
              {errors.title && (
                <span className="error-text">{errors.title}</span>
              )}
            </div>

            <div className="form-group">
              <label>Slug (url-friendly) *</label>
              <input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
              />
              {errors.slug && <span className="error-text">{errors.slug}</span>}
            </div>

            <div className="form-group">
              <label>Excerpt (krótki opis – max 280 znaków) *</label>
              <textarea
                name="excerpt"
                value={formData.excerpt}
                onChange={handleChange}
                maxLength={280}
                rows={3}
              />
              {errors.excerpt && (
                <span className="error-text">{errors.excerpt}</span>
              )}
            </div>

            <div className="form-group">
              <label>Treść wpisu (markdown / plain text) *</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleChange}
                rows={12}
              />
              {errors.content && (
                <span className="error-text">{errors.content}</span>
              )}
            </div>

            <div className="form-group">
              <label>Kategoria *</label>
              <input
                name="category"
                value={formData.category}
                onChange={handleChange}
              />
              {errors.category && (
                <span className="error-text">{errors.category}</span>
              )}
            </div>

            <div className="form-group">
              <label>Tagi (oddziel przecinkami)</label>
              <input
                name="tags"
                value={formData.tags}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="PUBLISHED">Opublikowany</option>
                <option value="ARCHIVED">Zarchiwizowany</option>
              </select>
            </div>

            <div className="form-group">
              <label>Data publikacji *</label>
              <input
                type="date"
                name="publishedAt"
                value={formData.publishedAt}
                onChange={handleChange}
              />
              {errors.publishedAt && (
                <span className="error-text">{errors.publishedAt}</span>
              )}
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                />
                Wyróżniony wpis
              </label>
            </div>

            <div className="form-group">
              <label>Zdjęcie wyróżniające (opcjonalne)</label>
              <div
                className="image-dropzone"
                onClick={() => fileInputRef.current?.click()}
              >
                {formData.featuredImage ? (
                  <img
                    src={URL.createObjectURL(formData.featuredImage)}
                    alt="Podgląd"
                  />
                ) : post?.featuredImageUrl ? (
                  <img src={post.featuredImageUrl} alt="Aktualne" />
                ) : (
                  <span>Kliknij lub przeciągnij zdjęcie</span>
                )}
              </div>
              <input
                type="file"
                name="featuredImage"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleChange}
                hidden
              />
            </div>

            {errors.submit && (
              <div className="error-text" style={{ marginBottom: 12 }}>
                {errors.submit}
              </div>
            )}

            <div className="form-actions">
              <button type="submit" className="submit-btn">
                Zapisz
              </button>
              <button type="button" className="cancel-btn" onClick={onClose}>
                Anuluj
              </button>
            </div>
          </form>
        ) : (
          <>
            {isAuthenticated && isEditing === false && (
              <button
                className="edit-btn"
                onClick={() => setIsEditing(true)}
                style={{ margin: '0 0 20px auto' }}
              >
                Edytuj wpis
              </button>
            )}
            {renderViewMode()}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogModal;
